import React, { useState, useEffect } from 'react';
import { Facebook, Users, Eye, Heart, MessageCircle, TrendingUp, UserPlus, UserMinus, ThumbsUp, ChevronDown, ChevronUp, Play, RefreshCw, Loader2 } from 'lucide-react';
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { analyticsAPI } from '../../../services/api';
import { useTheme } from '../../../contexts/ThemeContext';
import axios from 'axios';

const FacebookInsights = ({ clienteId, filtros, refreshTrigger }) => {
  const { darkMode } = useTheme();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [collapsed, setCollapsed] = useState({});
  const [topCommenters, setTopCommenters] = useState(null);
  const [importingComments, setImportingComments] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userComments, setUserComments] = useState([]);
  const [loadingUser, setLoadingUser] = useState(false);

  useEffect(() => {
    if (!clienteId) return;
    (async () => {
      try {
        setLoading(true);
        const params = {};
        if (filtros?.dataInicio) params.dataInicio = filtros.dataInicio;
        if (filtros?.dataFim) params.dataFim = filtros.dataFim;
        
        const [insightsRes, commentersRes] = await Promise.all([
          analyticsAPI.getFacebookInsights(clienteId, params),
          axios.get(`${process.env.REACT_APP_API_URL}/instagram-posts/comments/top-commenters/${clienteId}?platform=facebook`).catch(() => ({ data: [] })),
        ]);
        
        setData(insightsRes.data);
        setTopCommenters(commentersRes.data);
      } catch (e) {
        console.error('Erro ao carregar Facebook insights:', e);
      } finally {
        setLoading(false);
      }
    })();
  }, [clienteId, filtros?.dataInicio, filtros?.dataFim, refreshTrigger]);

  const handleImportComments = async () => {
    setImportingComments(true);
    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/instagram-posts/comments/import-facebook/${clienteId}`);
      const commentersRes = await axios.get(`${process.env.REACT_APP_API_URL}/instagram-posts/comments/top-commenters/${clienteId}?platform=facebook`);
      setTopCommenters(commentersRes.data);
    } catch (e) { console.error(e); }
    finally { setImportingComments(false); }
  };

  const openUserComments = async (username) => {
    setSelectedUser(username);
    setLoadingUser(true);
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/instagram-posts/comments/user/${clienteId}/${username}?platform=facebook`);
      setUserComments(res.data);
    } catch (e) { console.error(e); }
    finally { setLoadingUser(false); }
  };

  if (loading) return (
    <div className={`rounded-xl p-6 ${darkMode ? 'bg-zinc-800' : 'bg-white'} shadow`}>
      <div className="animate-pulse space-y-4">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-full ${darkMode ? 'bg-zinc-700' : 'bg-gray-200'}`} />
          <div className={`h-5 w-48 rounded ${darkMode ? 'bg-zinc-700' : 'bg-gray-200'}`} />
        </div>
        <div className="grid grid-cols-4 gap-3">
          {[1,2,3,4].map(i => <div key={i} className={`h-20 rounded-lg ${darkMode ? 'bg-zinc-700' : 'bg-gray-200'}`} />)}
        </div>
        <div className={`h-48 rounded-lg ${darkMode ? 'bg-zinc-700' : 'bg-gray-200'}`} />
      </div>
    </div>
  );

  if (!data?.contas?.length) return null;

  const cardBg = darkMode ? 'bg-zinc-800' : 'bg-white';
  const textPrimary = darkMode ? 'text-white' : 'text-gray-900';
  const textSecondary = darkMode ? 'text-gray-400' : 'text-gray-500';
  const gridBg = darkMode ? '#3f3f46' : '#e5e7eb';
  const tooltipBg = darkMode ? '#27272a' : '#fff';
  const tooltipBorder = darkMode ? '#3f3f46' : '#e5e7eb';

  const renderAccount = (conta) => {
    if (conta.error) return (
      <div key={conta.instancia_id} className={`rounded-xl p-5 ${cardBg} shadow`}>
        <p className="text-red-500 text-sm">❌ Erro: {conta.error}</p>
      </div>
    );

    const { profile, posts, insights, conversas } = conta;

    const getInsightSeries = (name) => {
      const metric = insights?.find(i => i.name === name);
      return (metric?.values || []).map(v => ({
        date: new Date(v.end_time).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }),
        value: v.value || 0,
      }));
    };

    const getInsightTotal = (name) => {
      const metric = insights?.find(i => i.name === name);
      if (!metric?.values) return 0;
      return metric.values.reduce((sum, v) => sum + (v.value || 0), 0);
    };

    const reachSeries = getInsightSeries('page_impressions_unique');
    const followsSeries = getInsightSeries('page_daily_follows');
    const unfollowsSeries = getInsightSeries('page_daily_unfollows');

    const totalReach = getInsightTotal('page_impressions_unique');
    const totalEngagements = getInsightTotal('page_post_engagements');
    const totalViews = getInsightTotal('page_views_total');
    const totalImpressions = getInsightTotal('page_posts_impressions');
    const totalVideoViews = getInsightTotal('page_video_views');
    const totalFollows = getInsightTotal('page_daily_follows');
    const totalUnfollows = getInsightTotal('page_daily_unfollows');
    const totalReactions = getInsightTotal('page_actions_post_reactions_like_total');

    // Totais dos posts
    const postTotalLikes = posts?.reduce((s, p) => s + p.likes, 0) || 0;
    const postTotalComments = posts?.reduce((s, p) => s + p.comments, 0) || 0;
    const postTotalShares = posts?.reduce((s, p) => s + p.shares, 0) || 0;

    // Conversas
    const totalConversas = parseInt(conversas?.total_conversas) || 0;
    const respondidas = parseInt(conversas?.respondidas) || 0;
    const porHumano = parseInt(conversas?.por_humano) || 0;
    const porIA = respondidas - porHumano;
    const naoRespondidas = totalConversas - respondidas;

    const pieData = [
      { name: 'Por humano', value: porHumano, color: '#3b82f6' },
      { name: 'Por IA', value: porIA, color: '#8b5cf6' },
      { name: 'Não respondidas', value: naoRespondidas, color: darkMode ? '#52525b' : '#d1d5db' },
    ].filter(d => d.value > 0);

    const statCards = [
      { label: 'Seguidores', value: profile.followers_count || profile.fan_count || 0, icon: Users, gradient: 'from-blue-500 to-blue-700' },
      { label: 'Alcance', value: totalReach, icon: Eye, gradient: 'from-cyan-500 to-blue-600' },
      { label: 'Visualizações', value: totalViews, icon: TrendingUp, gradient: 'from-indigo-500 to-violet-600' },
      { label: 'Engajamentos', value: totalEngagements, icon: Heart, gradient: 'from-pink-500 to-rose-600' },
      { label: 'Novos seguidores', value: totalFollows, icon: UserPlus, gradient: 'from-green-500 to-emerald-600', sub: totalUnfollows > 0 ? `-${totalUnfollows} unfollows` : null },
      { label: 'Reações', value: totalReactions, icon: ThumbsUp, gradient: 'from-amber-500 to-orange-600' },
    ];

    const pictureUrl = profile.picture?.data?.url;

    return (
      <div key={conta.instancia_id} className="space-y-4">
        {/* Header */}
        <div className={`rounded-xl p-5 ${cardBg} shadow`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {pictureUrl ? (
                <img src={pictureUrl} alt="" className="w-12 h-12 rounded-full ring-2 ring-blue-500/30" />
              ) : (
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center">
                  <Facebook className="w-6 h-6 text-white" />
                </div>
              )}
              <div>
                <h3 className={`text-lg font-bold ${textPrimary}`}>📘 Facebook — {profile.name}</h3>
                <p className={`text-sm ${textSecondary}`}>{profile.category} · {profile.fan_count || 0} curtidas · {posts?.length || 0} posts recentes</p>
              </div>
            </div>
            <button onClick={() => setCollapsed(prev => ({ ...prev, [conta.instancia_id]: !prev[conta.instancia_id] }))} className={`p-2 rounded-lg transition-colors ${darkMode ? 'hover:bg-zinc-700' : 'hover:bg-gray-100'}`}>
              {collapsed[conta.instancia_id] ? <ChevronDown className="w-5 h-5" /> : <ChevronUp className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {!collapsed[conta.instancia_id] && (
          <>
            {/* Stat Cards */}
            <div className="grid grid-cols-3 md:grid-cols-6 gap-2 sm:gap-3">
              {statCards.map((card, i) => {
                const Icon = card.icon;
                return (
                  <div key={i} className={`rounded-xl p-2 sm:p-3 ${cardBg} shadow`}>
                    <div className="flex items-center gap-1 mb-1 sm:mb-1.5">
                      <div className={`w-5 h-5 sm:w-6 sm:h-6 rounded-md bg-gradient-to-br ${card.gradient} flex items-center justify-center flex-shrink-0`}>
                        <Icon className="w-3 h-3 text-white" />
                      </div>
                      <span className={`text-[10px] sm:text-xs font-medium truncate ${textSecondary}`}>{card.label}</span>
                    </div>
                    <p className={`text-sm sm:text-lg font-bold ${textPrimary}`}>{card.value.toLocaleString('pt-BR')}</p>
                    {card.sub && <p className="text-xs text-red-500">{card.sub}</p>}
                  </div>
                );
              })}
            </div>

            {/* Gráficos */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              {/* Alcance */}
              <div className={`rounded-xl p-5 ${cardBg} shadow lg:col-span-2`}>
                <h4 className={`text-sm font-semibold mb-4 ${textPrimary}`}>
                  <Eye className="w-4 h-4 inline mr-1" /> Alcance Diário
                </h4>
                {reachSeries.length > 0 ? (
                  <ResponsiveContainer width="100%" height={220}>
                    <AreaChart data={reachSeries}>
                      <defs>
                        <linearGradient id="gradFbReach" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                          <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke={gridBg} />
                      <XAxis dataKey="date" tick={{ fontSize: 11, fill: darkMode ? '#9ca3af' : '#6b7280' }} interval="preserveStartEnd" />
                      <YAxis tick={{ fontSize: 11, fill: darkMode ? '#9ca3af' : '#6b7280' }} />
                      <Tooltip contentStyle={{ backgroundColor: tooltipBg, border: `1px solid ${tooltipBorder}`, borderRadius: 8, fontSize: 12 }} />
                      <Area type="monotone" dataKey="value" stroke="#3b82f6" fill="url(#gradFbReach)" strokeWidth={2} name="Alcance" />
                    </AreaChart>
                  </ResponsiveContainer>
                ) : (
                  <p className={`text-sm text-center py-10 ${textSecondary}`}>Sem dados de alcance disponíveis</p>
                )}
              </div>

              {/* Conversas Pie */}
              <div className={`rounded-xl p-5 ${cardBg} shadow`}>
                <h4 className={`text-sm font-semibold mb-4 ${textPrimary}`}>
                  <MessageCircle className="w-4 h-4 inline mr-1" /> Conversas Messenger
                </h4>
                {totalConversas > 0 ? (
                  <>
                    <ResponsiveContainer width="100%" height={160}>
                      <PieChart>
                        <Pie data={pieData} cx="50%" cy="50%" innerRadius={40} outerRadius={65} paddingAngle={3} dataKey="value">
                          {pieData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                        </Pie>
                        <Tooltip contentStyle={{ backgroundColor: tooltipBg, border: `1px solid ${tooltipBorder}`, borderRadius: 8, fontSize: 12 }} />
                      </PieChart>
                    </ResponsiveContainer>
                    <div className="space-y-1.5 mt-2">
                      {pieData.map((d, i) => (
                        <div key={i} className="flex items-center justify-between text-xs">
                          <div className="flex items-center gap-1.5">
                            <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: d.color }} />
                            <span className={textSecondary}>{d.name}</span>
                          </div>
                          <span className={`font-medium ${textPrimary}`}>{d.value}</span>
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  <p className={`text-sm text-center py-10 ${textSecondary}`}>Nenhuma conversa ainda</p>
                )}
                <div className={`mt-3 pt-3 border-t ${darkMode ? 'border-zinc-700' : 'border-gray-100'}`}>
                  <p className={`text-xs ${textSecondary}`}>Últimas 24h: <span className={`font-semibold ${textPrimary}`}>{conversas?.ultimas_24h || 0}</span></p>
                </div>
              </div>
            </div>

            {/* Follows/Unfollows */}
            {followsSeries.length > 2 && (
              <div className={`rounded-xl p-5 ${cardBg} shadow`}>
                <h4 className={`text-sm font-semibold mb-4 ${textPrimary}`}>
                  <UserPlus className="w-4 h-4 inline mr-1" /> Seguidores / Unfollows
                </h4>
                <ResponsiveContainer width="100%" height={180}>
                  <BarChart data={followsSeries.map((f, i) => ({
                    date: f.date,
                    follows: f.value,
                    unfollows: -(unfollowsSeries[i]?.value || 0),
                  }))}>
                    <CartesianGrid strokeDasharray="3 3" stroke={gridBg} />
                    <XAxis dataKey="date" tick={{ fontSize: 11, fill: darkMode ? '#9ca3af' : '#6b7280' }} interval="preserveStartEnd" />
                    <YAxis tick={{ fontSize: 11, fill: darkMode ? '#9ca3af' : '#6b7280' }} />
                    <Tooltip contentStyle={{ backgroundColor: tooltipBg, border: `1px solid ${tooltipBorder}`, borderRadius: 8, fontSize: 12 }} />
                    <Bar dataKey="follows" fill="#22c55e" name="Novos" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="unfollows" fill="#ef4444" name="Unfollows" radius={[0, 0, 4, 4]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* Posts Recentes */}
            {posts?.length > 0 && (
              <div className={`rounded-xl p-5 ${cardBg} shadow`}>
                <h4 className={`text-sm font-semibold mb-4 ${textPrimary}`}>
                  📝 Posts Recentes ({posts.length})
                  <span className={`ml-3 text-xs font-normal ${textSecondary}`}>
                    Total: {postTotalLikes} curtidas · {postTotalComments} comentários · {postTotalShares} compartilhamentos
                  </span>
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {posts.slice(0, 9).map(post => (
                    <div key={post.id} className={`rounded-lg border p-3 ${darkMode ? 'border-zinc-700 bg-zinc-900/50' : 'border-gray-100 bg-gray-50'}`}>
                      {post.full_picture && (
                        <img src={post.full_picture} alt="" className="w-full h-32 object-cover rounded-md mb-2" />
                      )}
                      <p className={`text-xs line-clamp-2 mb-2 ${textPrimary}`}>{post.message || '(sem texto)'}</p>
                      <div className="flex items-center gap-3 text-xs">
                        <span className={textSecondary}>❤️ {post.likes}</span>
                        <span className={textSecondary}>💬 {post.comments}</span>
                        <span className={textSecondary}>🔄 {post.shares}</span>
                      </div>
                      <p className={`text-xs mt-1 ${textSecondary}`}>{new Date(post.created_time).toLocaleDateString('pt-BR')}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  const CommentersSection = () => {
    const cardBg = darkMode ? 'bg-zinc-800' : 'bg-white';
    const textPrimary = darkMode ? 'text-white' : 'text-gray-900';
    const textSecondary = darkMode ? 'text-gray-400' : 'text-gray-500';

    const sentimentEmoji = { positivo: '😊', neutro: '😐', negativo: '😞' };

    return (
      <>
      <div className={`rounded-xl p-6 ${cardBg} shadow mt-6`}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <MessageCircle className="w-5 h-5 text-blue-500" />
            <h3 className={`font-semibold ${textPrimary}`}>Top Comentadores</h3>
          </div>
          <button onClick={handleImportComments} disabled={importingComments}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition ${
              darkMode ? 'bg-zinc-700 text-gray-300 hover:bg-zinc-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}>
            {importingComments ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
            Sincronizar Comentários
          </button>
        </div>
        {!topCommenters?.length ? (
          <p className={`text-sm ${textSecondary}`}>Nenhum comentário importado ainda. Clique em "Sincronizar Comentários".</p>
        ) : (
          <div className="space-y-2">
            {topCommenters.slice(0, 15).map((c, i) => (
              <div key={c.username} onClick={() => openUserComments(c.username)}
                className={`flex items-center gap-3 p-2.5 rounded-lg cursor-pointer transition ${darkMode ? 'hover:bg-zinc-700/50' : 'hover:bg-gray-50'}`}>
                <span className={`w-6 text-center text-sm font-bold ${i < 3 ? 'text-blue-500' : textSecondary}`}>{i + 1}</span>
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center text-white text-xs font-bold">
                  {c.username?.charAt(0)?.toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-medium truncate ${textPrimary}`}>{c.username}</p>
                </div>
                <div className="flex items-center gap-3 text-xs">
                  <span className={`font-semibold ${textPrimary}`}>{c.total} 💬</span>
                  {Number(c.positivos) > 0 && <span className="text-green-500">{c.positivos} 😊</span>}
                  {Number(c.negativos) > 0 && <span className="text-red-500">{c.negativos} 😞</span>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal de comentários do usuário */}
      {selectedUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setSelectedUser(null)}>
          <div className={`w-full max-w-2xl rounded-2xl ${cardBg} shadow-2xl max-h-[80vh] flex flex-col`} onClick={e => e.stopPropagation()}>
            <div className={`flex items-center justify-between p-5 border-b ${darkMode ? 'border-zinc-700' : 'border-gray-200'}`}>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center text-white font-bold">
                  {selectedUser.charAt(0).toUpperCase()}
                </div>
                <div>
                  <p className={`font-semibold ${textPrimary}`}>{selectedUser}</p>
                  <p className={`text-xs ${textSecondary}`}>{userComments.length} comentário(s)</p>
                </div>
              </div>
              <button onClick={() => setSelectedUser(null)} className={`p-2 rounded-lg transition ${darkMode ? 'hover:bg-zinc-700' : 'hover:bg-gray-100'}`}>
                <span className="text-lg">✕</span>
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-5 space-y-3">
              {loadingUser ? (
                <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-blue-500" /></div>
              ) : userComments.map((c, i) => (
                <div key={i} className={`p-4 rounded-xl border ${darkMode ? 'border-zinc-700 bg-zinc-900/50' : 'border-gray-100 bg-gray-50'}`}>
                  <div className="flex items-start gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className={`text-xs px-2 py-0.5 rounded-full ${darkMode ? 'bg-zinc-700 text-gray-300' : 'bg-gray-200 text-gray-600'}`}>Facebook</span>
                        {c.sentimento && <span className="text-xs">{sentimentEmoji[c.sentimento] || ''}</span>}
                        <span className={`text-xs ${textSecondary}`}>{new Date(c.commented_at).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit' })}</span>
                      </div>
                      <p className={`text-sm mt-1 ${textPrimary}`}>"{c.text}"</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      </>
    );
  };

  return (
    <>
      {data.contas.map(renderAccount)}
      <CommentersSection />
    </>
  );
};

export default FacebookInsights;
